/**
 * The full Bokapsys permission catalog. Codes mirror the seed migrations:
 *   - 1700000000028_rbac_core.js
 *   - 1700000000040_platform_admin_permissions.js
 *   - 1700000000041_tenant_settings_and_perms.js
 *   - 1700000000044_force_reauth_and_session_timeout.js
 *   - 1700000000047_agents_workflows.js
 *
 * Adding a new code = update this union AND ship the migration in the
 * same PR. The TypeScript union forces drift to surface as a type error
 * rather than silently failing at runtime.
 */

export const PERMISSIONS = [
  // statements
  'statements.upload',
  'statements.view',
  'statements.analyze',
  'statements.manage',
  'statements.history',
  // accounting
  'accounting.journal_entries',
  'accounting.coa',
  'accounting.reconcile',
  'accounting.accruals',
  'accounting.reporting',
  'accounting.reporting_config',
  'accounting.year_end_close',
  'accounting.tb_import',
  'accounting.allocations',
  'accounting.budget',
  'accounting.schedules',
  // banking
  'banking.plaid',
  // invoicing - AR phase 1 (see migration 1700000000058_invoicing_core_ar.js)
  'invoicing.customers.view',
  'invoicing.customers.manage',
  'invoicing.invoices.view',
  'invoicing.invoices.create',
  'invoicing.invoices.post',
  'invoicing.invoices.send',
  'invoicing.invoices.void',
  'invoicing.payments.record',
  // invoicing - Phase 2b reminders + recurring
  'invoicing.reminders.manage',
  'invoicing.recurring.manage',
  // invoicing - Phase 2c credit notes, refunds, write-offs
  'invoicing.credits.view',
  'invoicing.credits.create',
  'invoicing.credits.apply',
  'invoicing.refunds.create',
  'invoicing.write_offs.create',
  // invoicing - Phase 2d customer portal + AR aging
  'invoicing.customer_portal.manage',
  'invoicing.ar.aging.view',
  // invoicing - AP (seeded in Phase 1, UI activates in Phase 4)
  'invoicing.vendors.view',
  'invoicing.vendors.manage',
  'invoicing.bills.view',
  'invoicing.bills.create',
  // invoicing - Phase 4 AP foundation
  'invoicing.bills.post',
  'invoicing.bills.void',
  'invoicing.bills.approve',
  'invoicing.bills.pay',
  'invoicing.vendors.bank.view',
  'invoicing.vendors.bank.manage',
  'invoicing.ap.aging.view',
  // invoicing - Phase 5 OCR + exceptions
  'invoicing.bills.ingest',
  'invoicing.bills.extraction.review',
  'invoicing.exceptions.view',
  'invoicing.exceptions.resolve',
  'invoicing.ai.suggestions.apply',
  // invoicing - Phase 6 payment orchestration
  'invoicing.payments.schedule',
  'invoicing.payments.release',
  'invoicing.payments.cancel',
  'invoicing.payment_batches.view',
  'invoicing.payment_batches.manage',
  'invoicing.remittance.send',
  // invoicing - Phase 7 AI
  'invoicing.ai.autoapprove.config',
  'invoicing.ai.collections.draft',
  'invoicing.ai.anomaly.view',
  'invoicing.ai.forecast.view',
  // invoicing - Phase 8 enterprise controls
  'invoicing.sod.config',
  'invoicing.audit.export',
  'invoicing.multi_entity.manage',
  'invoicing.tax.config',
  // invoicing - Feature 2.2 unified payments dashboard (Connect + managed)
  'invoicing.payouts.view',
  // client intake
  'client.intake',
  // ai
  'ai.chatbot',
  'chatbot.share.all',
  'agents.view',
  'agents.manage',
  'workflows.view',
  'workflows.manage',
  'workflows.run',
  'workflows.audit.view',
  // dashboard, profile
  'dashboard.view',
  'profile.view',
  'profile.edit',
  // billing
  'billing.view',
  'billing.manage',
  // notifications
  'notifications.view',
  // team / api keys / audit / data
  'team.view',
  'team.invite',
  'team.manage',
  'team.roles',
  'team.ai_gateway.manage',
  'api_keys.manage',
  'audit.view',
  'audit.delete',
  'data.export',
  // approvals - legacy placeholder codes (kept for backwards compat with
  // any UI references; Phase 3 ships the richer vocabulary below).
  'approvals.view',
  'approvals.decide',
  'approvals.configure',
  // approvals - Phase 3a approval workflow engine
  'approvals.inbox.view',
  'approvals.act',
  'approvals.delegate',
  'approvals.policies.view',
  'approvals.policies.manage',
  'approvals.escalate',
  'approvals.override',
  // inbox - Gmail OAuth, email fetch, extraction
  'inbox.view',
  'inbox.connect',
  'inbox.disconnect',
  'inbox.sync',
  'inbox.onboard',
  'inbox.items.manage',
  'inbox.autosync.manage',
  'inbox.categories.manage',
  'inbox.rules.manage',
  // engines core (shared between Workforce OS + Tax OS)
  'engines.rule_packs.view',
  'engines.disclaimers.view',
  'engines.events.view',
  'engines.ai.usage.view',
  // Workforce OS - People
  'payroll.dashboard.view',
  'payroll.employees.view',
  'payroll.employees.manage',
  'payroll.employees.terminate',
  'payroll.employees.delete',
  'payroll.contractors.view',
  'payroll.contractors.manage',
  'payroll.bank_change',
  'payroll.documents.view',
  'payroll.documents.manage',
  // Workforce OS - Compensation
  'payroll.compensation.view',
  'payroll.compensation.manage',
  'payroll.components.view',
  'payroll.components.manage',
  'payroll.formulas.publish',
  'payroll.grades.manage',
  'payroll.equity.view',
  'payroll.equity.manage',
  'payroll.equity.accelerate',
  'payroll.cycles.manage',
  // Workforce OS - Time & Leave
  'payroll.time.view',
  'payroll.time.manage',
  'payroll.time.approve',
  'payroll.leave.view',
  'payroll.leave.manage',
  'payroll.leave.approve',
  'payroll.holidays.manage',
  'payroll.schedules.manage',
  // Workforce OS - Pay runs
  'payroll.runs.view',
  'payroll.runs.create',
  'payroll.runs.simulate',
  'payroll.runs.approve',
  'payroll.runs.post',
  'payroll.runs.reverse',
  'payroll.runs.off_cycle',
  'payroll.runs.correction',
  // Workforce OS - Payslips
  'payroll.payslips.view',
  'payroll.payslips.view_own',
  // Workforce OS - Benefits
  'payroll.benefits.view',
  'payroll.benefits.manage',
  'payroll.benefits.enroll',
  'payroll.benefits.integrations.manage',
  'payroll.retirement.manage',
  'payroll.hsa_fsa.manage',
  'payroll.cobra.manage',
  'payroll.aca.view',
  'payroll.aca.manage',
  // Workforce OS - Payments
  'payroll.payments.view',
  'payroll.payments.batch',
  'payroll.payments.transmit',
  'payroll.payments.checks',
  'payroll.payments.returns',
  // Workforce OS - Garnishments
  'payroll.garnishments.view',
  'payroll.garnishments.manage',
  // Workforce OS - Reports, sandbox, migration, compliance, settings
  'payroll.reports.view',
  'payroll.reports.export',
  'payroll.sandbox.view',
  'payroll.sandbox.manage',
  'payroll.migration.run',
  'payroll.settings.view',
  'payroll.settings.manage',
  'payroll.custom_fields.manage',
  'payroll.compliance.view',
  'payroll.compliance.manage',
  'payroll.consent.manage',
  'payroll.retention.manage',
  // Workforce OS - Industry packs (Phase 2)
  'payroll.industry.healthcare.manage',
  'payroll.industry.construction.manage',
  'payroll.industry.transportation.manage',
  'payroll.industry.hospitality.manage',
  'payroll.industry.education.manage',
  'payroll.industry.union.manage',
  // Tax OS - Filings
  'tax.dashboard.view',
  'tax.filings.view',
  'tax.filings.draft',
  'tax.filings.review',
  'tax.filings.sign',
  'tax.filings.file',
  'tax.filings.amend',
  // Tax OS - Payments
  'tax.payments.view',
  'tax.payments.schedule',
  'tax.payments.release',
  // Tax OS - Jurisdictions + registrations
  'tax.jurisdictions.view',
  'tax.jurisdictions.manage',
  'tax.registrations.view',
  'tax.registrations.manage',
  // Tax OS - Sales tax (Phase 3)
  'tax.sales.view',
  'tax.sales.manage',
  'tax.sales.nexus.view',
  'tax.sales.nexus.manage',
  'tax.sales.exemptions.manage',
  // Tax OS - Business / personal (Phase 3)
  'tax.business.view',
  'tax.business.manage',
  'tax.personal.view',
  'tax.personal.manage',
  // Tax OS - Calendar, reconciliation, reports
  'tax.calendar.view',
  'tax.calendar.manage',
  'tax.reconciliation.view',
  'tax.reconciliation.run',
  'tax.reports.view',
  'tax.reports.export',
  // Tax OS - AI assistant
  'tax.assistant.use',
  'tax.estimate.view',
  'tax.compliance.view',
  // Tax OS - Documents + settings
  'tax.documents.view',
  'tax.documents.manage',
  'tax.settings.view',
  'tax.settings.manage',
  // Firm practice (Phase 4)
  'firm.clients.view',
  'firm.clients.manage',
  'firm.clients.impersonate',
  'firm.bulk_filing',
  'firm.review.assign',
  // tenant settings
  'tenant.settings.view',
  'tenant.settings.edit',
  'tenant.session.edit',
  // workspace danger zone (tenant super_admin only by default)
  'workspace.delete',
  // security / support
  'security.manage',
  'support.contact_sales',
  // platform-scope (admin.*)
  'admin.tenants',
  'admin.tenants.view',
  'admin.tenants.manage',
  'admin.users',
  'admin.users.view',
  'admin.users.manage',
  'admin.subscriptions.manage',
  'admin.audit_all',
  'admin.audit.view',
  'admin.audit.export',
  'admin.audit.delete',
  'admin.staff.manage',
  'admin.staff.invite',
  'admin.staff.roles',
  'admin.staff.delete',
  'admin.api_keys',
  'admin.tools',
  'admin.impersonate',
  // Feature 2.2 - Bokapsys staff process tenant payouts on managed mode.
  // Migration 101 split the legacy `admin.managed_payouts` into `.view`
  // (read-only dashboard + drill-downs) and `.manage` (process payouts +
  // override per-tenant fee). Routes demand the granular code.
  'admin.managed_payouts.view',
  'admin.managed_payouts.manage',
  // Migration 104 - "Contact us" submissions (sales / bug / feature /
  // support). View = read the inbox; manage = update status, add notes,
  // delete.
  'admin.contact_messages.view',
  'admin.contact_messages.manage',
  // Migration 110 - Release-notes announcements broadcast to every
  // active customer user (in-app + email).
  'admin.announcements.view',
  'admin.announcements.manage',
  // Platform-only engines core (migration 124)
  'admin.engines.rule_packs.publish',
  'admin.engines.disclaimers.manage',
  'admin.engines.events.replay',
  // Migration 136 - industry, migration, webhooks, firm, ESS
  'me.payslips.view',
  'me.tax_forms.view',
  'me.w4.manage',
  'me.direct_deposit.manage',
  'webhooks.view',
  'webhooks.manage',
  'admin.platform.rule_packs.publish',
] as const

export type PermissionCode = (typeof PERMISSIONS)[number]

export const PLATFORM_TENANT_ID = '00000000-0000-0000-0000-000000000001'

export type Role =
  | 'super_admin'
  | 'admin'
  | 'manager'
  | 'cfo'
  | 'bookkeeper'
  | 'auditor'
  | 'user'
  | (string & {})
